import { CUIDEI_ROUTES, CuideiStep } from 'constants/cuidei'
import { useRouter } from 'next/router'
import { useCallback } from 'react'
import { resetCuideiFlow } from 'redux/slices/cuidei-slice'
import { useAppDispatch, useAppSelector } from 'redux/store'

/**
 * Steps that belong to an ACTIVE consultation. Navigating to the flow while in
 * one of these must RESUME it (the patient is queued / in consent), never reset
 * — a reset would walk them back into Dados/Pagamento and charge them again.
 */
const ACTIVE_CONSULT_STEPS: CuideiStep[] = [CuideiStep.FilaVideo, CuideiStep.Consentimento]

/**
 * Entry point for every "Nova consulta" CTA (navbar "+", consultations hero).
 *
 * The flow step persists in Redux across sessions, so after a finished
 * consultation it is parked on a post-flow step (Conta/Feedback). Navigating
 * to the flow without resetting re-rendered that stale step — patients hitting
 * "Iniciar nova consulta" landed on "Crie sua senha de acesso" with no purchase
 * flow (QA 2026-07-08). Reset first (keeping pharmacy attribution), except when
 * an active consultation step is in progress.
 */
/**
 * Returns a callback that resets a stale persisted flow (keeping attribution),
 * unless an active consultation step is in progress. Shared by the "Nova
 * consulta" CTAs and the pathology landing CTA (which navigates via its link).
 */
export const useResetStaleCuideiFlow = () => {
  const dispatch = useAppDispatch()
  const step = useAppSelector((state) => state.cuidei?.step ?? CuideiStep.Dados)
  const consultationId = useAppSelector((state) => state.cuidei?.consultation?.id)
  const consultationState = useAppSelector((state) => state.cuidei?.consultation?.state)

  return useCallback(() => {
    // A "FilaVideo" step only counts as active while the consultation itself
    // is still alive — after it finishes/cancels the persisted step is a
    // leftover and '+' must start a NEW flow, not resume the ended queue
    // (QA 2026-07-09).
    const consultationEnded = !consultationState || ['finished', 'canceled'].includes(consultationState)
    const hasPaidOnboarding =
      step === CuideiStep.Finalizar && consultationId && !['finished', 'canceled'].includes(consultationState ?? '')
    const isActive = hasPaidOnboarding || (ACTIVE_CONSULT_STEPS.includes(step) && !consultationEnded)
    if (!isActive) dispatch(resetCuideiFlow())
  }, [dispatch, step, consultationId, consultationState])
}

export const useStartNewCuideiConsultation = () => {
  const router = useRouter()
  const resetStaleFlow = useResetStaleCuideiFlow()

  return useCallback(() => {
    resetStaleFlow()
    router.push(CUIDEI_ROUTES.flow)
  }, [resetStaleFlow, router])
}
